import type { ProjectModelDraft } from '../../../project/model';
import { effectiveVectorSourceForLayer } from '../../../joins/effectiveLayer';
import { isLayerLabelConfigurationValid } from '../../../labels/validation';
import { buildStaticCompositionPlan } from '../planning';
import { assertStaticMapFrameSupported, type StaticWebGlCapabilities } from '../webGlCapabilities';
import { projectionFramePlan } from './projection';
import { staticBasemapAttributionText } from '../../../basemaps/catalog';

export function hasVectorPdfLabels(project: ProjectModelDraft): boolean {
  return project.layers.some((layer) => {
    if (!layer.visible || !layer.labels.enabled || !layer.labels.field) return false;
    const source = effectiveVectorSourceForLayer(project.sources, layer);
    return !!source && isLayerLabelConfigurationValid(layer.labels, source.fields);
  });
}

export function buildVectorPdfPlan(
  project: ProjectModelDraft,
  dpi: number,
  mapLogicalPixelsPerMm?: number,
) {
  const attribution = staticBasemapAttributionText(project.map.basemap);
  // At 72 units/inch this physical plan is in PDF points, independent of raster detail.
  const page = buildStaticCompositionPlan(
    project.composition,
    project.sources,
    project.layers,
    72,
    'vector',
    attribution,
    mapLogicalPixelsPerMm,
    project.annotationLayers,
  );
  const raster = buildStaticCompositionPlan(
    project.composition,
    project.sources,
    project.layers,
    dpi,
    'vector',
    attribution,
    mapLogicalPixelsPerMm,
    project.annotationLayers,
  );
  return {
    page,
    raster,
    labels: hasVectorPdfLabels(project),
    basemap: project.map.basemap.presetId !== 'none',
  };
}

export function assertVectorPdfSupported(
  plan: ReturnType<typeof buildVectorPdfPlan>,
  capabilities: StaticWebGlCapabilities | null,
): void {
  assertStaticMapFrameSupported(projectionFramePlan(plan.page), capabilities);
  if (plan.labels || plan.basemap) assertStaticMapFrameSupported(plan.raster, capabilities);
}
